import type { FC, ReactNode } from "react"
import { useMemo, useState } from "react"

import { Dialog, DialogPanel } from "@headlessui/react"
import { ArrowLongRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import type { SupplementalAttemptDto } from "~/api/dto/contract-package.dto"
import Button from "~/components/Button"
import { SpinnerCircle } from "~/components/Spinner"
import type { ContractVersionHistoryItem } from "~/containers/Proposal/state/contracts"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { Permission } from "~/graphql/client"
import type { User, Contract, Maybe } from "~/graphql/client"
import { formatCurrency } from "~/utils/finance/format"

import type { PendingVersionAlert } from "./pending-version-alert"
import { SupplementalPackageAlerts } from "./SupplementalPackageAlerts"

type ContractPackageStatus = `not_sent` | `pending` | `signed`

interface ContractPackageData {
  id: string
  name: string
  contractType: string
  version: number
  sentDate: string | null
  signedDate: string | null
  status: ContractPackageStatus
}

interface ContractSummary {
  totalSystemSize: number
  totalCost: number
  signedCount: number
  totalCount: number
  statusText: string
  canSendNewDocs: boolean
}

interface ContractStatusModalProps {
  isOpen: boolean
  onClose: () => void
  onSendDocs: () => void
  onResend?: (contractId: string) => void
  onSendSupplemental?: (contractId: string) => void
  resendingContractId?: string | null
  sendingSupplementalId?: string | null
  resendSuccessId?: string | null
  sendSupplementalSuccessId?: string | null
  onClearResendSuccess?: () => void
  onClearSendSupplementalSuccess?: () => void
  disabled?: boolean
  contracts: Contract[]
  versionHistory: ContractVersionHistoryItem[]
  pendingVersionAlert?: PendingVersionAlert | null
  isLoading?: boolean
  errorMessages?: ReactNode[]
  supplementalAttempts?: SupplementalAttemptDto[]
  onRetrySupplementalSend?: (attemptId: string) => void
  pendingSupplementalAttemptId?: string | null
  supplementalActionError?: string | null
  supplementalActionErrorAttemptId?: string | null
}

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  primary: `Primary Contract`,
  supplemental: `Supplemental Contract`,
}

const STATUS_STYLES: Record<ContractPackageStatus, string> = {
  signed: `bg-green-100 text-green-700`,
  pending: `bg-amber-100 text-amber-600`,
  not_sent: `bg-gray-100 text-gray-500`,
}

const STATUS_TEXT: Record<ContractPackageStatus, string> = {
  signed: `Signed`,
  pending: `Pending`,
  not_sent: `Not Sent`,
}

const formatContractDate = (dateString: string | null): string => {
  if (!dateString) return `—`
  return format(new Date(dateString), `MM/dd/yyyy`)
}

/**
 * A bookkeeping row rather than a signing package the seller can act on.
 *
 * Provider-owned flows (LightReach, Concert, GoodLeap TPO) record a shell
 * Contract with no DocuSign envelope, purely to pin the date the provider sent
 * its own main contract and to anchor the package version. The finance partner
 * owns that document and its signatures — Vision has nothing to resend and
 * nothing to remind anyone about, so `resendContract` rejects it outright.
 * Listing it offered an action that could only ever fail (VIS-565 §10).
 *
 * Deliberately not "has no envelope": a legacy staged supplemental also has no
 * envelope yet, but it is genuinely sendable. The shell is distinguished by
 * being marked sent while Vision holds no envelope for it.
 */
const isProviderBookkeepingShell = (contract: Contract): boolean =>
  !contract.envelopeId && !!contract.dateSent

const getContractStatus = (contract: Contract): ContractPackageStatus => {
  if (contract.dateSigned) return `signed`
  if (contract.dateSent) return `pending`
  return `not_sent`
}

const transformContract = (contract: Contract): ContractPackageData => ({
  id: contract.id,
  name: CONTRACT_TYPE_LABELS[contract.contractType ?? ``] ?? `Contract`,
  contractType: contract.contractType ?? `primary`,
  version: contract.contractPackageVersion ?? 1,
  sentDate: contract.dateSent ?? null,
  signedDate: contract.dateSigned ?? null,
  status: getContractStatus(contract),
})

const calculateSummary = (
  contracts: Contract[],
  packages: ContractPackageData[],
): ContractSummary => {
  const primaryContract = contracts.find((c) => c.contractType === `primary`)
  const signedCount = contracts.filter((c) => c.dateSigned).length
  const totalCount = contracts.length

  // A provider-owned package has no Vision primary to read size and cost from,
  // so fall back to whichever package carries them.
  const contractWithFigures =
    primaryContract?.systemSize != null || primaryContract?.totalCost != null
      ? primaryContract
      : contracts.find((c) => c.systemSize != null || c.totalCost != null)

  return {
    totalSystemSize: contractWithFigures?.systemSize ?? 0,
    totalCost: contractWithFigures?.totalCost ?? 0,
    signedCount,
    totalCount,
    statusText: `${signedCount} of ${totalCount} Signed`,
    canSendNewDocs: true,
  }
}

const determineIfAccountIdDemo = (
  seller: User,
  mentor: Maybe<User>,
): boolean => {
  const mentorIsDemo = mentor?.permissions.includes(Permission.limited_demo)
  if (mentorIsDemo) {
    return mentorIsDemo
  }

  return seller.permissions.includes(Permission.limited_demo)
}

/** Hoisted so a default prop never re-renders the alerts on every parent render. */
const EMPTY_SUPPLEMENTAL_ATTEMPTS: SupplementalAttemptDto[] = []

export const ContractStatusModal: FC<ContractStatusModalProps> = ({
  isOpen,
  onClose,
  onSendDocs,
  onResend,
  onSendSupplemental,
  resendingContractId,
  sendingSupplementalId,
  resendSuccessId,
  sendSupplementalSuccessId,
  onClearResendSuccess,
  onClearSendSupplementalSuccess,
  disabled = false,
  contracts,
  versionHistory,
  pendingVersionAlert = null,
  isLoading = false,
  errorMessages = [],
  supplementalAttempts = EMPTY_SUPPLEMENTAL_ATTEMPTS,
  onRetrySupplementalSend,
  pendingSupplementalAttemptId = null,
  supplementalActionError = null,
  supplementalActionErrorAttemptId = null,
}) => {
  const { t } = useTranslation()
  const proposl = useProposalContext()
  const [isVersionHistoryExpanded, setIsVersionHistoryExpanded] = useState(false)

  // Bookkeeping shells are not packages the seller can act on.
  const signingPackages = useMemo(
    () => contracts.filter((contract) => !isProviderBookkeepingShell(contract)),
    [contracts],
  )
  const hasContracts = signingPackages.length > 0
  const packages = useMemo(
    () => signingPackages.map(transformContract),
    [signingPackages],
  )

  const summary = useMemo(
    () => (hasContracts ? calculateSummary(signingPackages, packages) : null),
    [signingPackages, packages, hasContracts],
  )

  const hasErrors = errorMessages.length > 0
  const isDemo = determineIfAccountIdDemo(
    proposl.local.seller,
    proposl.local.mentor,
  )
  const fallbackRecipientEmail = proposl.local.homeowner.email?.trim() || null
  const versionHistoryLabel =
    versionHistory.length === 1 ? `version` : `versions`
  const canSend = hasContracts ? summary?.canSendNewDocs : true
  const buttonText = hasContracts ? t(`sendNew`) : t(`send`)

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-[9999]">
      <div
        className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="fixed inset-0 z-[10000] flex items-end justify-center sm:items-center sm:p-4">
        <DialogPanel className="flex max-h-[90vh] w-full max-w-[900px] flex-col overflow-hidden rounded-t-3xl bg-white shadow-lg sm:rounded-2xl">
          {/* Header */}
          <div className="flex-shrink-0 px-12 pt-12 pb-6">
            <h2 className="text-3xl leading-tight font-bold text-blue-600">
              {t(`contractStatus`)}
            </h2>
            <p className="mt-2 text-base text-gray-500">
              {hasContracts ? `Status of contract packages.` : t(`noContracts`)}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-12">
            {resendSuccessId && (
              <div className="mb-6">
                <div className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
                  <span>Contract resent successfully.</span>
                  <button
                    type="button"
                    onClick={onClearResendSuccess}
                    className="ml-4 text-green-700 hover:text-green-900"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}
            {sendSupplementalSuccessId && (
              <div className="mb-6">
                <div className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
                  <span>Supplemental contract sent successfully.</span>
                  <button
                    type="button"
                    onClick={onClearSendSupplementalSuccess}
                    className="ml-4 text-green-700 hover:text-green-900"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}
            <SupplementalPackageAlerts
              attempts={supplementalAttempts}
              onRetrySend={onRetrySupplementalSend}
              pendingAttemptId={pendingSupplementalAttemptId}
              actionError={supplementalActionError}
              actionErrorAttemptId={supplementalActionErrorAttemptId}
            />
            {hasErrors && (
              <div className="mb-6 space-y-2">
                {errorMessages.map((message, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
                  >
                    {message}
                  </div>
                ))}
              </div>
            )}
            {hasContracts && summary && (
              <>
                <div className="mb-6">
                  <div className="grid grid-cols-3 gap-8 text-center">
                    <div>
                      <div className="mb-2 text-sm font-medium text-blue-600">
                        {t(`systemSize`)}
                      </div>
                      <div className="text-lg font-bold text-gray-600">
                        {summary.totalSystemSize} kW
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-medium text-blue-600">
                        {t(`totalCost`)}
                      </div>
                      <div className="text-lg font-bold text-gray-600">
                        {formatCurrency(summary.totalCost)}
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-sm font-medium text-blue-600">
                        {t(`status`)}
                      </div>
                      <div className="text-lg font-bold text-gray-600">
                        {summary.statusText}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  {/* Table Header */}
                  <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4 rounded-t-lg bg-gray-100 px-4 py-3">
                    <div className="text-sm font-bold text-blue-600">
                      Package
                    </div>
                    <div className="text-sm font-bold text-blue-600">
                      {t(`dateSent`)}
                    </div>
                    <div className="text-sm font-bold text-blue-600">
                      {t(`dateSigned`)}
                    </div>
                    <div className="text-sm font-bold text-blue-600">
                      {t(`status`)}
                    </div>
                    <div className="text-right text-sm font-bold text-blue-600">
                      Action
                    </div>
                  </div>

                  {/* Table Rows */}
                  {packages.map((pkg, index) => (
                    <div
                      key={pkg.id}
                      className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr] items-center gap-4 px-4 py-5 ${
                        index < packages.length - 1
                          ? `border-b border-gray-200`
                          : ``
                      }`}
                    >
                      <div className="flex items-center gap-2 text-base text-gray-900">
                        {pkg.name}
                        <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-600">
                          v{pkg.version}
                        </span>
                      </div>
                      <div className="text-base text-gray-600">
                        {formatContractDate(pkg.sentDate)}
                      </div>
                      <div className="text-base text-gray-600">
                        {formatContractDate(pkg.signedDate)}
                      </div>
                      <div>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium ${
                            STATUS_STYLES[pkg.status]
                          }`}
                        >
                          {STATUS_TEXT[pkg.status]}
                        </span>
                      </div>
                      <div className="text-right">
                        {pkg.status === `not_sent` ? (
                          <button
                            type="button"
                            className="rounded-lg border-2 border-blue-600 bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={sendingSupplementalId === pkg.id}
                            onClick={() => onSendSupplemental?.(pkg.id)}
                          >
                            <span className="flex items-center gap-2">
                              {sendingSupplementalId === pkg.id
                                ? `Sending...`
                                : `Send`}
                              {sendingSupplementalId === pkg.id && (
                                <SpinnerCircle
                                  size={16}
                                  backgroundColor="rgba(255, 255, 255, 0.4)"
                                  spinnerColor="rgba(255, 255, 255, 0.9)"
                                />
                              )}
                            </span>
                          </button>
                        ) : pkg.status === `pending` ? (
                          <button
                            type="button"
                            className="rounded-lg border-2 border-blue-600 bg-white px-4 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={resendingContractId === pkg.id}
                            onClick={() => onResend?.(pkg.id)}
                          >
                            <span className="flex items-center gap-2">
                              {resendingContractId === pkg.id
                                ? `Resending...`
                                : `Resend`}
                              {resendingContractId === pkg.id && (
                                <SpinnerCircle
                                  size={16}
                                  backgroundColor="rgba(37, 99, 235, 0.2)"
                                  spinnerColor="rgba(37, 99, 235, 0.8)"
                                />
                              )}
                            </span>
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {versionHistory.length > 0 && (
                  <div className="mb-8 border-t border-gray-200 pt-5">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-md px-1 py-2 text-left"
                      onClick={() =>
                        setIsVersionHistoryExpanded((previous) => !previous)
                      }
                    >
                      <div className="text-sm font-semibold text-gray-600">
                        {`Version History (${versionHistory.length} ${versionHistoryLabel})`}
                      </div>
                      <ChevronDownIcon
                        className={`h-5 w-5 text-gray-500 transition-transform ${
                          isVersionHistoryExpanded ? `rotate-180` : ``
                        }`}
                      />
                    </button>

                    <div
                      className={`grid overflow-hidden transition-[grid-template-rows,opacity,margin] duration-200 ease-out ${
                        isVersionHistoryExpanded
                          ? `mt-2 grid-rows-[1fr] opacity-100`
                          : `mt-0 grid-rows-[0fr] opacity-0`
                      }`}
                      aria-hidden={!isVersionHistoryExpanded}
                    >
                      <div className="min-h-0">
                        <div className="space-y-2">
                          {versionHistory.map((historyItem) => {
                            const historyRecipientEmail =
                              historyItem.recipientEmail?.trim() || null
                            const recipientEmailToDisplay =
                              historyRecipientEmail ??
                              fallbackRecipientEmail ??
                              `—`

                            return (
                              <div
                                key={historyItem.versionNumber}
                                className="grid grid-cols-[1.8fr_0.9fr_2.1fr] items-center gap-4 rounded-md border-l-4 border-gray-300 bg-gray-100 px-4 py-3"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-700">
                                    {`Version ${historyItem.versionNumber}`}
                                  </span>
                                  <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-gray-200 text-gray-500">
                                    Void
                                  </span>
                                  {historyItem.isSigned && (
                                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700">
                                      Signed
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {historyItem.systemSize ?? `—`} kW •{` `}
                                  {historyItem.totalCost !== null
                                    ? formatCurrency(historyItem.totalCost)
                                    : `—`}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="flex w-full items-center gap-1.5">
                                    <span>
                                      {formatContractDate(historyItem.sentAt)}
                                    </span>
                                    <ArrowLongRightIcon
                                      className="h-4 w-4 flex-shrink-0 text-gray-500"
                                      aria-hidden="true"
                                    />
                                    <span className="min-w-0 break-words">
                                      {recipientEmailToDisplay}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex-shrink-0 border-t border-gray-200 px-12 py-6"
            style={{ backgroundColor: `var(--blue-form-footer)` }}
          >
            <div className="flex items-center justify-between gap-4">
              <Button type="button" onClick={onClose}>
                {t(`cancel`)}
              </Button>
              <div className="flex items-center gap-6">
                {pendingVersionAlert && summary && (
                  <div className="text-right">
                    <div className="text-sm font-semibold text-blue-700">
                      New version ready to send
                    </div>
                    <div className="text-sm font-medium text-blue-700">
                      {pendingVersionAlert.systemSize} kW •{` `}
                      {formatCurrency(pendingVersionAlert.totalCost)}
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="solid"
                  disabled={
                    disabled || isLoading || hasErrors || !canSend || isDemo
                  }
                  blocked={
                    disabled || isLoading || hasErrors || !canSend || isDemo
                  }
                  onClick={onSendDocs}
                >
                  <span className="flex items-center gap-2">
                    {isLoading ? t(`sending`) : buttonText}
                    {isLoading && (
                      <SpinnerCircle
                        size={20}
                        backgroundColor="rgba(255, 255, 255, 0.4)"
                        spinnerColor="rgba(255, 255, 255, 0.9)"
                      />
                    )}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
